import * as React from 'react';
import {BrowserRouter as Router, Route, Routes} from 'react-router-dom';
import './theme/variables.css';
import AppFallback from './Components/Common/AppFallback/AppFallback';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { SidebarContainer } from './Components/Common/SidebarPlugin/SidebarContainer';
import { useAppDispatch, useAppSelector } from './hooks/redux';
import { restoreAuthorizationFromToken } from './redux/authorizationSlice';

// Lazy load route components
const Login = React.lazy(() => import('./Components/Pages/Login/Login'));
const Main = React.lazy(() => import('./Components/Pages/Main/Main'));
const SignIn = React.lazy(() => import('./Components/Pages/Login/SignIn/SignIn'));
const SignUp = React.lazy(() => import('./Components/Pages/Login/SignUp/SignUp'));
const ResetPassword = React.lazy(() => import('./Components/Pages/Login/ResetPassword/ResetPassword'));
const Controls = React.lazy(() => import('./Components/Pages/Controls'));
const ClientSignIn = React.lazy(() => import('./Components/Pages/Login/Client/SignIn'));
const AdminInviteConfirmation = React.lazy(() => import('./Components/Pages/Public/AdminInviteConfirmation/AdminInviteConfirmation'));
const StockOwnerInviteConfirmation = React.lazy(() => import('./Components/Pages/Public/StockOwnerInviteConfirmation/StockOwnerInviteConfirmation'));
const MobileRedirectInfo = React.lazy(() => import('./Components/Pages/Public/MobileRedirectInfo/MobileRedirectInfo'));
const SupportLoginPage = React.lazy(() => import('./Components/Pages/Login/SupportLogin/SupportLoginPage'));

// Component to render appropriate SignIn based on hostname
const SignInRouter = () => {
  const isClientDomain = window.location.hostname === 'clients.flowgistic.com'
  return isClientDomain ? <ClientSignIn /> : <SignIn />;
};

export const App = () => {
  useDocumentTitle();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector((state) => state.user.info.accessToken);

  React.useEffect(() => {
    dispatch(restoreAuthorizationFromToken(accessToken || undefined));
  }, [accessToken, dispatch]);

  return (
    <Router>
      <SidebarContainer />
      <React.Suspense fallback={<AppFallback />}>
        <Routes>
          {/* Public routes - NO authentication required */}
          <Route path="/public/admin-invite" element={<AdminInviteConfirmation />} />
          <Route path="/p/s-invite" element={<StockOwnerInviteConfirmation />} />
          <Route path="/p/mobile-info" element={<MobileRedirectInfo />} />
          <Route path="/public/mobile-info" element={<MobileRedirectInfo />} />
           <Route path="/setup-password" element={<ResetPassword step={2} />} />

          {/* Auth routes */}
          <Route path="/login/support" element={<SupportLoginPage />} />
          <Route path="/login/sign-in" element={<SignInRouter />} />
          <Route path="/login/sign-up" element={<SignUp />} />
          <Route path="/client/sign-in" element={<ClientSignIn />} />
          <Route path="/login/sign-up/:ID" element={<SignUp />} />
          <Route path="/login/reset-email" element={<ResetPassword step={1} />} />
          <Route path="/login/reset-password" element={<ResetPassword step={2} />} />
          <Route path="/password/reset/:token" element={<Login password />} />
          <Route path="/controls" element={<Controls />} />

          {/* Protected routes - authentication required */}
          <Route path="*" element={<Main />} />
        </Routes>
      </React.Suspense>
    </Router>
  );
};

export default App;
